/**
 * MarkDistributionEditor — Hierarchical mark distribution editor
 *
 * Data shape (rows):
 *   [
 *     { topic, marks, podcastId, children: [{ topic, marks, podcastId }] },
 *     ...
 *   ]
 *
 * Three types of main topic:
 *   1. Grouping header  → marks = "", children > 0  (e.g. "General Knowledge")
 *   2. Standalone topic → marks = "15", children = [] (e.g. "Current Affairs")
 *   3. Either can optionally link a podcast
 *
 * Fixes:
 *   - Uses version="v2" (react-select) so options always render correctly
 *   - menuPortalTarget={document.body} + menuPosition="fixed" prevents dropdown clipping
 *   - Delete main topic cascades (children are inside the row object — removed together)
 */
import { useState } from 'react';
import { notifier } from 'components/Toaster';
import PodcastDropdown from 'components/molecules/PodcastDropdown';
import { useDeleteMarkDistributionRow } from 'hooks/API/POST/useManageExamInfo';

// Shared react-select portal props — prevents menu clipping inside overflow containers
const PORTAL_PROPS = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({ ...base, minHeight: 36, fontSize: 13 }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    indicatorsContainer: (base) => ({ ...base, height: 36 }),
  },
};

export default function MarkDistributionEditor({
  examTypeId,
  rows,
  onChange,
  podcastOptions = [],
  podcastsLoading = false,
  podcastsError = false,
}) {
  // Track which rowId is currently being deleted (for per-row loading state)
  const [deletingId, setDeletingId] = useState(null);
  const { mutate: deleteRow } = useDeleteMarkDistributionRow();

  // ── Main-topic mutations ────────────────────────────────────────────────────
  const addMainTopic = () =>
    onChange([...rows, { topic: '', marks: '', podcastId: '', children: [] }]);

  const updateMain = (idx, field, value) =>
    onChange(rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));

  /**
   * Delete a main topic.
   * - If it has a DB _id → call DELETE API (backend cascades children), then update local state.
   * - If it's a brand-new unsaved row → just remove from local state.
   */
  const removeMain = (idx) => {
    const row = rows[idx];
    if (row._id) {
      setDeletingId(row._id);
      deleteRow(
        { examTypeId, rowId: row._id },
        {
          onSuccess: () => {
            onChange(rows.filter((_, i) => i !== idx));
            setDeletingId(null);
            notifier.success('Topic deleted');
          },
          onError: () => {
            notifier.error('Delete failed');
            setDeletingId(null);
          },
        }
      );
    } else {
      // Not yet saved — remove locally
      onChange(rows.filter((_, i) => i !== idx));
    }
  };

  // ── Sub-topic mutations ─────────────────────────────────────────────────────
  const addChild = (parentIdx) =>
    onChange(
      rows.map((r, i) =>
        i === parentIdx
          ? { ...r, children: [...r.children, { topic: '', marks: '', podcastId: '' }] }
          : r
      )
    );

  const updateChild = (parentIdx, childIdx, field, value) =>
    onChange(
      rows.map((r, i) =>
        i === parentIdx
          ? {
              ...r,
              children: r.children.map((c, j) => (j === childIdx ? { ...c, [field]: value } : c)),
            }
          : r
      )
    );

  /**
   * Delete a sub-topic.
   * - If it has a DB _id → call DELETE API, then update local state.
   * - If it's unsaved → just remove from local state.
   */
  const removeChild = (parentIdx, childIdx) => {
    const child = rows[parentIdx].children[childIdx];
    const applyLocalRemove = () =>
      onChange(
        rows.map((r, i) =>
          i === parentIdx ? { ...r, children: r.children.filter((_, j) => j !== childIdx) } : r
        )
      );

    if (child._id) {
      setDeletingId(child._id);
      deleteRow(
        { examTypeId, rowId: child._id },
        {
          onSuccess: () => {
            applyLocalRemove();
            setDeletingId(null);
            notifier.success('Sub-topic deleted');
          },
          onError: () => {
            notifier.error('Delete failed');
            setDeletingId(null);
          },
        }
      );
    } else {
      applyLocalRemove();
    }
  };

  return (
    <div>
      {rows.length === 0 && (
        <p className="text-muted mb-3" style={{ fontSize: 13 }}>
          No topics yet. Click "+ Add Main Topic" below.
        </p>
      )}

      {rows.map((row, idx) => (
        <div key={idx} style={styles.mainCard}>
          {/* ── Main topic row ────────────────────────────────────────────── */}
          <div style={styles.mainHeader}>
            <span style={styles.mainLabel}>Main Topic {idx + 1}</span>
            <button
              onClick={() => removeMain(idx)}
              style={{
                ...styles.removeBtnRed,
                opacity: deletingId === row._id ? 0.6 : 1,
                cursor: deletingId ? 'not-allowed' : 'pointer',
              }}
              disabled={!!deletingId}
              title="Delete this topic and all its sub-topics"
            >
              {deletingId === row._id ? '…' : '✕ Delete'}
            </button>
          </div>

          <div style={styles.mainInputRow}>
            {/* Topic name */}
            <div style={{ flex: 3 }}>
              <label style={styles.fieldLabel}>Topic Name</label>
              <input
                value={row.topic}
                onChange={(e) => updateMain(idx, 'topic', e.target.value)}
                placeholder="e.g. General Knowledge / Current Affairs"
                style={styles.input}
              />
            </div>

            {/* Marks — blank = grouping header */}
            <div style={{ width: 100, flexShrink: 0 }}>
              <label style={styles.fieldLabel}>
                Marks <span style={{ color: '#aaa', fontWeight: 400 }}>(blank if header)</span>
              </label>
              <input
                type="number"
                min={0}
                value={row.marks}
                onChange={(e) => updateMain(idx, 'marks', e.target.value)}
                placeholder="e.g. 15"
                style={{ ...styles.input, textAlign: 'center' }}
              />
            </div>

            {/* Podcast link */}
            <div style={{ flex: 2, minWidth: 180 }}>
              <PodcastDropdown
                version="v2"
                label="Linked Podcast (optional)"
                value={row.podcastId}
                options={podcastOptions}
                isLoading={podcastsLoading}
                isError={podcastsError}
                placeholder="— None —"
                handleChange={(e) => updateMain(idx, 'podcastId', e.target.value)}
                {...PORTAL_PROPS}
              />
            </div>
          </div>

          {/* ── Sub-topics ────────────────────────────────────────────────── */}
          {row.children && row.children.length > 0 && (
            <div style={styles.childrenArea}>
              <div style={styles.childTableHeader}>
                <span style={{ ...styles.childColLabel, flex: 3 }}>Sub-topic</span>
                <span style={{ ...styles.childColLabel, width: 100 }}>Marks</span>
                <span style={{ ...styles.childColLabel, flex: 2 }}>Linked Podcast</span>
                <span style={{ width: 80 }} />
              </div>

              {row.children.map((child, cIdx) => (
                <div key={cIdx} style={styles.childRow}>
                  <span style={styles.indentArrow}>↳</span>

                  {/* Sub-topic name */}
                  <div style={{ flex: 3, minWidth: 0 }}>
                    <input
                      value={child.topic}
                      onChange={(e) => updateChild(idx, cIdx, 'topic', e.target.value)}
                      placeholder="e.g. History of Kerala"
                      style={styles.input}
                    />
                  </div>

                  {/* Sub-topic marks */}
                  <div style={{ width: 100, flexShrink: 0 }}>
                    <input
                      type="number"
                      min={0}
                      value={child.marks}
                      onChange={(e) => updateChild(idx, cIdx, 'marks', e.target.value)}
                      placeholder="5"
                      style={{ ...styles.input, textAlign: 'center' }}
                    />
                  </div>

                  {/* Sub-topic podcast */}
                  <div style={{ flex: 2, minWidth: 180 }}>
                    <PodcastDropdown
                      version="v2"
                      label=""
                      value={child.podcastId}
                      options={podcastOptions}
                      isLoading={podcastsLoading}
                      isError={podcastsError}
                      placeholder="— None —"
                      handleChange={(e) => updateChild(idx, cIdx, 'podcastId', e.target.value)}
                      {...PORTAL_PROPS}
                    />
                  </div>

                  <button
                    onClick={() => removeChild(idx, cIdx)}
                    style={{
                      ...styles.removeBtnIcon,
                      opacity: deletingId === child._id ? 0.6 : 1,
                      cursor: deletingId ? 'not-allowed' : 'pointer',
                    }}
                    disabled={!!deletingId}
                    title="Delete sub-topic"
                  >
                    {deletingId === child._id ? '…' : '✕'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ── Add sub-topic ─────────────────────────────────────────────── */}
          <div style={{ padding: '8px 16px 12px' }}>
            <button onClick={() => addChild(idx)} style={styles.addChildBtn}>
              + Add Sub-topic
            </button>
          </div>
        </div>
      ))}

      <button onClick={addMainTopic} style={styles.addMainBtn}>
        + Add Main Topic
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  mainCard: {
    marginBottom: 12,
    border: '1px solid #dde1e7',
    borderRadius: 8,
    background: '#fff',
    overflow: 'visible', // ← must be visible so react-select menus aren't clipped
  },
  mainHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px 8px',
    background: '#f5f7fa',
    borderBottom: '1px solid #e8eaed',
    borderRadius: '8px 8px 0 0',
  },
  mainLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: '#3f4f6e',
  },
  mainInputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 12,
    padding: '12px 16px',
    flexWrap: 'nowrap',
  },
  childrenArea: {
    borderTop: '1px solid #eef0f4',
    padding: '10px 16px 4px 16px',
    background: '#fafbfc',
  },
  childTableHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingLeft: 22,
    marginBottom: 6,
  },
  childColLabel: {
    fontSize: 11,
    color: '#888',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  childRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 12,
    marginBottom: 8,
  },
  indentArrow: {
    color: '#aab',
    fontSize: 15,
    width: 18,
    flexShrink: 0,
    paddingBottom: 6,
  },
  fieldLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#555',
    marginBottom: 4,
  },
  input: {
    padding: '7px 10px',
    borderRadius: 4,
    border: '1px solid #d0d0d0',
    fontSize: 13,
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
  },
  addChildBtn: {
    padding: '5px 14px',
    borderRadius: 4,
    border: '1px solid #4caf50',
    background: '#f1fdf3',
    color: '#2e7d32',
    cursor: 'pointer',
    fontSize: 12,
  },
  addMainBtn: {
    marginTop: 4,
    padding: '8px 20px',
    borderRadius: 4,
    border: '1px dashed #999',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 13,
    color: '#555',
  },
  removeBtnRed: {
    color: '#c62828',
    border: '1px solid #ef9a9a',
    background: '#fff5f5',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 12,
    padding: '3px 10px',
  },
  removeBtnIcon: {
    color: '#e53935',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: 16,
    flexShrink: 0,
    padding: '2px 4px',
    marginBottom: 4,
  },
};
